import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CustomAlert from "@common/components/Feedback/CustomAlert";
import { TermsAndPrivacyIcon } from "@common/utils/icons/icons";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import NavigationRoutes from "@common/enums/query-params/sphere-navigation-routes";
import { PageTabs } from "../legal/LegalPage";
import { parseStringFromEnum, TAB_QUERY_STRING } from "utils/url-tabs-helper";

export const PRIVACY_EMAIL = "privacy@kemaro.ch";

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Stack spacing={1.5}>
    <Typography variant="h2" fontSize={18}>
      {title}
    </Typography>
    {children}
  </Stack>
);

const AccountDataPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const selfServiceSteps = [
    t("account-data-self-service-step-1"),
    t("account-data-self-service-step-2"),
    t("account-data-self-service-step-3"),
    t("account-data-self-service-step-4"),
  ];

  const whatHappensItems = [
    t("account-data-what-happens-1"),
    t("account-data-what-happens-2"),
    t("account-data-what-happens-3"),
    t("account-data-what-happens-4"),
    t("account-data-what-happens-5"),
    t("account-data-what-happens-6"),
  ];

  const deletionRequestMailTo = `mailto:${PRIVACY_EMAIL}?subject=${encodeURIComponent(
    t("account-data-email-subject")
  )}&body=${encodeURIComponent(t("account-data-email-body"))}`;

  const privacyPolicyClickHandler = () => {
    navigate({
      pathname: NavigationRoutes.Legal,
      search: `?${TAB_QUERY_STRING}=${parseStringFromEnum(PageTabs, PageTabs.PrivacyPolicy)}`,
    });
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }} data-testid="account-data-page">
      <Stack spacing={4}>
        <Stack spacing={1.5}>
          <Typography variant="h2" fontSize={24} lineHeight={1.5}>
            {t("account-data-title")}
          </Typography>
          <Typography>{t("account-data-intro")}</Typography>
        </Stack>

        <Divider />

        <Section title={t("account-data-self-service-title")}>
          <Typography>{t("account-data-self-service-description")}</Typography>
          <Box component="ol" sx={{ pl: 3, m: 0 }}>
            {selfServiceSteps.map((step) => (
              <Typography component="li" key={step} sx={{ mb: 1 }}>
                {step}
              </Typography>
            ))}
          </Box>
          <CustomAlert severity="info" header={t("account-data-owner-note")} />
        </Section>

        <Section title={t("account-data-email-title")}>
          <Typography>{t("account-data-email-description")}</Typography>
          <Box>
            <Button
              variant="contained"
              startIcon={<EmailRoundedIcon />}
              href={deletionRequestMailTo}
              data-testid="request-deletion-by-email-button"
            >
              {t("button-request-deletion-by-email")}
            </Button>
          </Box>
        </Section>

        <Section title={t("account-data-what-happens-title")}>
          <Box component="ul" sx={{ pl: 3, m: 0 }}>
            {whatHappensItems.map((item) => (
              <Typography component="li" key={item} sx={{ mb: 1 }}>
                {item}
              </Typography>
            ))}
          </Box>
        </Section>

        <Section title={t("account-data-not-deleted-title")}>
          <Typography>{t("account-data-not-deleted-description")}</Typography>
          <Typography>{t("account-data-sooner-note")}</Typography>
        </Section>

        <Section title={t("account-data-other-rights-title")}>
          <Typography>{t("account-data-other-rights-description")}</Typography>
        </Section>

        <Divider />

        <Stack spacing={1.5}>
          <Typography>{t("account-data-privacy-policy-description")}</Typography>
          <Box>
            <Button
              variant="outlined"
              startIcon={<TermsAndPrivacyIcon />}
              onClick={privacyPolicyClickHandler}
              data-testid="open-privacy-policy-button"
            >
              {t("button-open-privacy-policy")}
            </Button>
          </Box>
        </Stack>
      </Stack>
    </Container>
  );
};

export default AccountDataPage;
